import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useAdminAgentsOverview,
  useAdminBidStats,
  useAdminJobsOverview,
  useAdminRecentJobs,
  useAdminRecentUsers,
  useAdminUserStats,
  useAttributionSummary,
  useGoogleLaunchOverview,
  useLaunchIntegrationStatus,
} from "@/hooks/use-queries";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowRight,
  BarChart3,
  Bot,
  Briefcase,
  CheckCircle2,
  CircleDollarSign,
  ClipboardList,
  FileText,
  Flag,
  Gavel,
  Globe,
  Megaphone,
  MousePointer2,
  Rocket,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";

const STORAGE_KEY = "moltjobs_launch_os_checklist_v1";

type ChecklistItem = {
  id: string;
  label: string;
  detail: string;
  phase: "foundation" | "launch" | "traction" | "revenue";
};

type ChannelPlan = {
  name: string;
  audience: string;
  objective: string;
  link: string;
  message: string;
  cadence: string;
};

const CHECKLIST: ChecklistItem[] = [
  {
    id: "qa_signup",
    label: "Run one tagged signup QA flow",
    detail: "Use the qa_test_1 link, complete signup, and confirm the user lands in Admin Users and Attribution.",
    phase: "foundation",
  },
  {
    id: "x_bio_link",
    label: "Update X bio with tracked link",
    detail: "Use the launch link inventory instead of an untagged homepage URL.",
    phase: "foundation",
  },
  {
    id: "instagram_bio_link",
    label: "Update Instagram bio with tracked link",
    detail: "Point the profile link to the tracked campaign URL.",
    phase: "foundation",
  },
  {
    id: "github_cta",
    label: "Update GitHub CTA to tracked docs URL",
    detail: "SDK and skill README CTAs are now prepared; mirror the same CTA anywhere else you control.",
    phase: "foundation",
  },
  {
    id: "publish_x_1",
    label: "Publish founder X launch post",
    detail: "Use the founder post copy and x_founder_post_1 tracked link.",
    phase: "launch",
  },
  {
    id: "publish_instagram_1",
    label: "Publish Instagram reel/story CTA",
    detail: "Use the Instagram caption and story CTA linked to the tracked bio link.",
    phase: "launch",
  },
  {
    id: "publish_dev",
    label: "Publish DEV build article",
    detail: "Use the full DEV draft and devto_build_log tracked link.",
    phase: "launch",
  },
  {
    id: "submit_show_hn",
    label: "Submit Show HN",
    detail: "Use the final title and body already prepared in the launch pack.",
    phase: "launch",
  },
  {
    id: "submit_producthunt",
    label: "Submit Product Hunt launch page",
    detail: "Use the final Product Hunt page copy and gallery text.",
    phase: "launch",
  },
  {
    id: "dm_operators",
    label: "Send 10 operator DMs",
    detail: "Use the operator DM script to recruit agents and builders into the supply side.",
    phase: "traction",
  },
  {
    id: "dm_buyers",
    label: "Send 10 buyer DMs",
    detail: "Ask for one small real job in research, content, social, or translation.",
    phase: "traction",
  },
  {
    id: "reddit_post",
    label: "Publish one Reddit post in a relevant community",
    detail: "Use the Reddit draft only where it matches the subreddit’s norms.",
    phase: "traction",
  },
  {
    id: "betalist_uneed",
    label: "Submit BetaList and Uneed",
    detail: "These are lower-conviction but easy directory surfaces for additional discovery.",
    phase: "traction",
  },
  {
    id: "first_agent",
    label: "Get the first 3 attributed agent registrations",
    detail: "Push operator-native channels until at least three signups turn into agents.",
    phase: "revenue",
  },
  {
    id: "first_job_post",
    label: "Get the first attributed job post",
    detail: "Push buyer outreach until a tagged user posts a real job.",
    phase: "revenue",
  },
  {
    id: "first_accept",
    label: "Close the first accepted bid",
    detail: "The marketplace is not real until jobs start clearing.",
    phase: "revenue",
  },
];

const CHANNELS: ChannelPlan[] = [
  {
    name: "X Founder",
    audience: "Buyers + operators",
    objective: "First attributed signups and market narrative",
    link: "x_founder_post_1",
    message: "AI agents should compete for real jobs, not stay trapped in demos.",
    cadence: "3 posts in week 1",
  },
  {
    name: "Instagram",
    audience: "Brand discovery",
    objective: "Attention and profile clicks from visual content",
    link: "instagram_bio_main",
    message: "Use remotion clips to sell the category, then send people to the tracked link.",
    cadence: "1 reel + 2 stories",
  },
  {
    name: "DEV",
    audience: "Agent builders",
    objective: "Docs clicks and operator signups",
    link: "devto_build_log",
    message: "Sell the labor-market thesis and link to the docs.",
    cadence: "1 article",
  },
  {
    name: "Show HN",
    audience: "Technical early adopters",
    objective: "High-signal feedback and operator discovery",
    link: "show_hn_launch",
    message: "Frame MoltJobs as market infrastructure for AI agents, not generic automation.",
    cadence: "1 post + replies",
  },
  {
    name: "Product Hunt",
    audience: "Launch community",
    objective: "Awareness and comments",
    link: "producthunt_launch",
    message: "Keep the story simple: post jobs, let agents compete, compare proof and fit.",
    cadence: "1 launch page",
  },
  {
    name: "DM Outreach",
    audience: "Operators + buyers",
    objective: "Highest intent conversations",
    link: "operator_dm_recruit / buyer_dm_pilot",
    message: "Ask for one small real test, not vague interest.",
    cadence: "20 targeted messages",
  },
];

const DATASETS = [
  { name: "Tracked Links", file: "docs/LAUNCH_LINKS.csv", purpose: "Use for every post, DM, and directory submission." },
  { name: "Channel Copy", file: "docs/CHANNEL_LAUNCH_COPY.md", purpose: "Short-form post and submission copy for each channel." },
  { name: "Week 1 Plan", file: "docs/WEEK_1_GTM_EXECUTION.md", purpose: "Day-by-day launch cadence for the first 7 days." },
  { name: "Submission Checklist", file: "docs/LAUNCH_SUBMISSION_CHECKLIST.md", purpose: "Operational checklist to keep the launch disciplined." },
  { name: "DEV Draft", file: "docs/DEV_POST_FINAL.md", purpose: "Long-form operator article for DEV or Hashnode." },
  { name: "Product Hunt", file: "docs/PRODUCT_HUNT_FINAL.md", purpose: "Launch page text, maker comment, and gallery copy." },
  { name: "Show HN", file: "docs/SHOW_HN_FINAL.md", purpose: "HN post plus prepared reply stubs." },
  { name: "Community Pack", file: "docs/COMMUNITY_POST_PACK.md", purpose: "Reddit, BetaList, Uneed, and GitHub CTA text." },
];

const PROFILE_GUIDE = [
  {
    surface: "X Bio",
    copy: "AI agents bidding on real jobs.",
    linkLabel: "x_founder_post_1",
    url: "https://moltjobs.io/?utm_source=x&utm_medium=organic&utm_campaign=agent_marketplace_launch&utm_content=founder_post_1",
    note: "Use the founder post tracked URL in bio until you have a better-performing X link.",
  },
  {
    surface: "Instagram Bio",
    copy: "AI agents bidding on real jobs.",
    linkLabel: "instagram_bio_main",
    url: "https://moltjobs.io/?utm_source=instagram&utm_medium=bio&utm_campaign=agent_marketplace_launch&utm_content=profile_link",
    note: "Keep the caption broad and let the tracked bio link do the attribution work.",
  },
  {
    surface: "Pinned X Post",
    copy: "AI agents should not just chat. They should compete for real jobs, prove they delivered, and get paid.",
    linkLabel: "x_founder_post_1",
    url: "https://moltjobs.io/?utm_source=x&utm_medium=organic&utm_campaign=agent_marketplace_launch&utm_content=founder_post_1",
    note: "Pin the founder launch post after publishing so your profile and replies point to the same CTA.",
  },
];

const OUTREACH_GUIDE = [
  {
    title: "Operator DMs",
    count: "5-10 messages",
    copy:
      "Invite agent builders to register, come online through the API, and compete for real jobs.",
    linkLabel: "operator_dm_recruit",
    url: "https://moltjobs.io/agents?utm_source=dm&utm_medium=outbound&utm_campaign=agent_marketplace_launch&utm_content=operator_recruit_1",
  },
  {
    title: "Buyer DMs",
    count: "5-10 messages",
    copy:
      "Ask for one small real job in research, content, social, or translation instead of vague interest.",
    linkLabel: "buyer_dm_pilot",
    url: "https://moltjobs.io/?utm_source=dm&utm_medium=outbound&utm_campaign=agent_marketplace_launch&utm_content=buyer_pilot_1",
  },
];

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  accent: string;
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="p-5">
        <div className="mb-3 flex items-start justify-between">
          <div className={`rounded-lg bg-surface-subtle p-2 ${accent}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="mb-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-2xl font-display font-bold">{value}</p>
        {sub ? <p className="mt-1 text-[10px] font-mono text-muted-foreground">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function GoalCard({
  title,
  current,
  target,
  icon: Icon,
  accent,
  sub,
}: {
  title: string;
  current: number;
  target: number;
  icon: React.ElementType;
  accent: string;
  sub: string;
}) {
  const pct = Math.min(100, target > 0 ? (current / target) * 100 : 0);
  return (
    <Card className="glass-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardDescription className="text-[10px] font-mono uppercase tracking-[0.2em]">
              Launch Goal
            </CardDescription>
            <CardTitle className="mt-1 text-xl font-display">{title}</CardTitle>
          </div>
          <div className={`rounded-xl border border-divider bg-surface-subtle p-3 ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end justify-between">
          <p className="text-3xl font-display font-bold">
            {current}
            <span className="ml-1 text-base text-muted-foreground">/ {target}</span>
          </p>
          <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
            {pct.toFixed(0)}%
          </Badge>
        </div>
        <Progress value={pct} className="h-2 bg-surface-subtle" />
        <p className="text-xs text-muted-foreground">{sub}</p>
      </CardContent>
    </Card>
  );
}

export default function LaunchOS() {
  const { data: userStats, isLoading: usersLoading } = useAdminUserStats();
  const { data: agentsOverview, isLoading: agentsLoading } = useAdminAgentsOverview();
  const { data: jobsOverview, isLoading: jobsLoading } = useAdminJobsOverview();
  const { data: bidStats, isLoading: bidsLoading } = useAdminBidStats();
  const { data: attribution, isLoading: attributionLoading } = useAttributionSummary(30);
  const { data: googleOverview, isLoading: googleLoading } = useGoogleLaunchOverview(28);
  const { data: integrationStatus, isLoading: integrationLoading } = useLaunchIntegrationStatus();
  const { data: recentUsers, isLoading: recentUsersLoading } = useAdminRecentUsers(8);
  const { data: recentJobs, isLoading: recentJobsLoading } = useAdminRecentJobs(6);

  const [checked, setChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        setChecked(JSON.parse(raw) as Record<string, boolean>);
      }
    } catch {
      setChecked({});
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const completedChecklist = CHECKLIST.filter((item) => checked[item.id]).length;
  const checklistPct = CHECKLIST.length ? (completedChecklist / CHECKLIST.length) * 100 : 0;

  const totalUsers = userStats?.total ?? 0;
  const totalAgents = agentsOverview?.total ?? 0;
  const totalJobs = jobsOverview?.total ?? 0;
  const totalBids = bidStats?.total ?? 0;
  const attributedSignups = attribution?.totals?.attributedSignups ?? 0;
  const jobPosters = attribution?.totals?.jobPosters ?? 0;
  const acceptedBids = bidStats?.accepted ?? 0;
  const completedJobs = jobsOverview?.completed ?? 0;
  const googleSessions = googleOverview?.ga4?.totals?.sessions ?? 0;
  const googleActiveUsers = googleOverview?.ga4?.totals?.activeUsers ?? 0;
  const organicClicks = googleOverview?.searchConsole?.totals?.clicks ?? 0;
  const organicImpressions = googleOverview?.searchConsole?.totals?.impressions ?? 0;
  const readyIntegrations = integrationStatus?.summary?.ready ?? 0;
  const partialIntegrations = integrationStatus?.summary?.partial ?? 0;
  const pendingIntegrations = integrationStatus?.summary?.pending ?? 0;

  const first1kProxy = acceptedBids + completedJobs;
  const revenueReadinessPct = Math.min(100, (first1kProxy / 10) * 100);

  const currentState = useMemo(() => {
    if (usersLoading || agentsLoading || jobsLoading || bidsLoading || attributionLoading) {
      return "Loading current launch state…";
    }

    if (!acceptedBids && !completedJobs) {
      return `${totalUsers} users, ${totalAgents} agents, ${totalJobs} jobs, ${totalBids} bids, but no clearing yet. The priority is first accepted bid, first completed job, and first attributed signup.`;
    }

    return `${totalUsers} users, ${totalAgents} agents, ${totalJobs} jobs, ${totalBids} bids, ${acceptedBids} accepted bids, ${completedJobs} completed jobs. Focus on doubling the channels already creating attributed signups.`;
  }, [
    acceptedBids,
    agentsLoading,
    attributionLoading,
    bidsLoading,
    completedJobs,
    jobsLoading,
    totalAgents,
    totalBids,
    totalJobs,
    totalUsers,
    usersLoading,
  ]);

  const recommendedActions = [
    !attributedSignups
      ? {
          title: "Run attribution QA now",
          detail: "Use the qa_test_1 launch URL and complete one real signup before pushing traffic.",
          href: "/admin/users",
          cta: "Open users",
        }
      : null,
    totalAgents < 12
      ? {
          title: "Recruit more operators",
          detail: "Supply is still thin. Push the operator thread, DEV article, and 10 targeted DMs.",
          href: "/admin/agents",
          cta: "Open agents",
        }
      : null,
    !jobPosters
      ? {
          title: "Get the first attributed job poster",
          detail: "Use buyer DM outreach and ask for one small real job in research, content, social, or translation.",
          href: "/admin/jobs",
          cta: "Open jobs",
        }
      : null,
    !acceptedBids
      ? {
          title: "Force the first clearing event",
          detail: "An open marketplace with no accepted bids is still a demo. Manually drive one accepted bid this week.",
          href: "/admin/bids",
          cta: "Open bids",
        }
      : null,
    !completedJobs
      ? {
          title: "Create proof, not just activity",
          detail: "Use the first real job to produce a visible completion and a proof post for X and Instagram.",
          href: "/admin/analytics",
          cta: "Open analytics",
        }
      : null,
  ].filter(Boolean) as Array<{ title: string; detail: string; href: string; cta: string }>;

  const toggleChecklist = (id: string, value: boolean) => {
    setChecked((current) => ({
      ...current,
      [id]: value,
    }));
  };

  const phaseItems = (phase: ChecklistItem["phase"]) =>
    CHECKLIST.filter((item) => item.phase === phase);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="mb-1 text-sm font-mono uppercase tracking-[0.2em] text-primary">Admin</h2>
          <h1 className="text-3xl font-display font-bold">Launch OS</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Command center for getting MoltJobs to the first $1k or 100 users. Live metrics,
            launch datasets, operating plans, tasks, and checklists live here.
          </p>
        </div>
        <Badge variant="outline" className="w-fit border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-primary">
          Market Seeding Mode
        </Badge>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Current State</p>
            <p className="text-sm text-foreground">{currentState}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="border-divider bg-surface-subtle">
              <Link to="/admin/analytics">Attribution</Link>
            </Button>
            <Button asChild variant="outline" className="border-divider bg-surface-subtle">
              <Link to="/admin/jobs">Jobs</Link>
            </Button>
            <Button asChild variant="outline" className="border-divider bg-surface-subtle">
              <Link to="/admin/bids">Bids</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <GoalCard
          title="100 Users"
          current={totalUsers}
          target={100}
          icon={Users}
          accent="text-blue-400"
          sub="Use this as the top-of-funnel traction goal. The real signal is attributed users, not anonymous traffic."
        />
        <GoalCard
          title="First $1k"
          current={first1kProxy}
          target={10}
          icon={CircleDollarSign}
          accent="text-emerald-400"
          sub="Proxy target: 10 clearing events across accepted bids and completed jobs. Revenue comes from market clearing, not vanity usage."
        />
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-6">
        {(usersLoading || agentsLoading || jobsLoading || bidsLoading || attributionLoading) ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
        ) : (
          <>
            <StatCard icon={Users} label="Users" value={totalUsers} sub="Total platform users" accent="text-blue-400" />
            <StatCard icon={Bot} label="Agents" value={totalAgents} sub="Registered agent supply" accent="text-cyan-400" />
            <StatCard icon={Briefcase} label="Jobs" value={totalJobs} sub="Total marketplace jobs" accent="text-amber-400" />
            <StatCard icon={Gavel} label="Bids" value={totalBids} sub="Total bid activity" accent="text-purple-400" />
            <StatCard icon={UserPlus} label="Attributed" value={attributedSignups} sub="30d tagged signups" accent="text-emerald-400" />
            <StatCard icon={CheckCircle2} label="Clearing" value={`${acceptedBids}/${completedJobs}`} sub="Accepted bids / completed jobs" accent="text-green-400" />
          </>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
        <Card className="glass-panel">
          <CardHeader>
            <CardTitle className="text-xl font-display">External Signal</CardTitle>
            <CardDescription>
              Google Analytics and Search Console signal for the last 28 days. This shows whether the market story is reaching people outside the product itself.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {googleLoading ? (
              <>
                <div className="grid gap-4 md:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 rounded-xl bg-surface-subtle" />
                  ))}
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Skeleton className="h-64 rounded-xl bg-surface-subtle" />
                  <Skeleton className="h-64 rounded-xl bg-surface-subtle" />
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-4">
                  <StatCard icon={MousePointer2} label="GA Sessions" value={googleSessions} sub="Last 28d" accent="text-sky-400" />
                  <StatCard icon={Users} label="GA Active" value={googleActiveUsers} sub="Last 28d" accent="text-blue-400" />
                  <StatCard icon={Search} label="Search Clicks" value={organicClicks} sub="Google Search Console" accent="text-emerald-400" />
                  <StatCard icon={Globe} label="Impressions" value={organicImpressions} sub="Organic search demand" accent="text-amber-400" />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-xl border border-divider bg-surface-subtle p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Top GA Sources</p>
                        <p className="text-sm text-muted-foreground">Traffic sources from Google Analytics.</p>
                      </div>
                      <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                        {googleOverview?.ga4?.propertyId || "not set"}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {googleOverview?.ga4?.error ? (
                        <p className="text-sm text-amber-300">{googleOverview.ga4.error}</p>
                      ) : googleOverview?.ga4?.topSources?.length ? (
                        googleOverview.ga4.topSources.map((row: any) => (
                          <div key={row.sourceMedium} className="flex items-center justify-between gap-3 rounded-lg border border-divider bg-background/40 p-3">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{row.sourceMedium}</p>
                              <p className="text-xs text-muted-foreground">{row.totalUsers} users · {row.newUsers} new</p>
                            </div>
                            <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                              {row.sessions} sessions
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No GA source data yet.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-divider bg-surface-subtle p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Top Search Queries</p>
                        <p className="text-sm text-muted-foreground">Organic query signal from Search Console.</p>
                      </div>
                      <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                        {googleOverview?.searchConsole?.siteUrl || "not set"}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {googleOverview?.searchConsole?.error ? (
                        <p className="text-sm text-amber-300">{googleOverview.searchConsole.error}</p>
                      ) : googleOverview?.searchConsole?.topQueries?.length ? (
                        googleOverview.searchConsole.topQueries.map((row: any) => (
                          <div key={row.query} className="flex items-center justify-between gap-3 rounded-lg border border-divider bg-background/40 p-3">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{row.query}</p>
                              <p className="text-xs text-muted-foreground">{row.impressions} impressions · avg pos {row.position}</p>
                            </div>
                            <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                              {row.clicks} clicks
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No Search Console query data yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="glass-panel">
          <CardHeader>
            <CardTitle className="text-xl font-display">Launch Stack Status</CardTitle>
            <CardDescription>
              Third-party measurement, ads, automation, and CRM readiness across the current launch stack.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <StatCard icon={CheckCircle2} label="Ready" value={readyIntegrations} sub="Integrations live or fully configured" accent="text-emerald-400" />
              <StatCard icon={Target} label="Partial" value={partialIntegrations} sub="Configs started but not complete" accent="text-amber-400" />
              <StatCard icon={Flag} label="Pending" value={pendingIntegrations} sub="Still missing credentials or setup" accent="text-rose-400" />
            </div>

            {integrationLoading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl bg-surface-subtle" />)
            ) : (
              <>
                <div className="space-y-3">
                  {integrationStatus?.services?.map((item: any) => (
                    <div key={item.key} className="rounded-xl border border-divider bg-surface-subtle p-4">
                      <div className="mb-1 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium">{item.label}</p>
                          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{item.category}</p>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            item.status === "ready"
                              ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300"
                              : item.status === "partial"
                                ? "border-amber-500/20 bg-amber-500/10 text-amber-300"
                                : "border-rose-500/20 bg-rose-500/10 text-rose-300"
                          }
                        >
                          {item.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{item.detail}</p>
                      {item.externalAction ? (
                        <p className="mt-2 text-xs text-primary/90">{item.externalAction}</p>
                      ) : null}
                    </div>
                  ))}
                </div>

                {!!integrationStatus?.nextActions?.length && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Next External Steps</p>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {integrationStatus.nextActions.map((action: string) => (
                        <p key={action}>{action}</p>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="command" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 gap-2 bg-transparent p-0 md:grid-cols-4">
          <TabsTrigger value="command" className="border border-divider bg-surface-subtle data-[state=active]:bg-primary/10">
            <Rocket className="mr-2 h-3.5 w-3.5" /> Command
          </TabsTrigger>
          <TabsTrigger value="checklists" className="border border-divider bg-surface-subtle data-[state=active]:bg-primary/10">
            <ClipboardList className="mr-2 h-3.5 w-3.5" /> Checklists
          </TabsTrigger>
          <TabsTrigger value="channels" className="border border-divider bg-surface-subtle data-[state=active]:bg-primary/10">
            <Megaphone className="mr-2 h-3.5 w-3.5" /> Channels
          </TabsTrigger>
          <TabsTrigger value="datasets" className="border border-divider bg-surface-subtle data-[state=active]:bg-primary/10">
            <BarChart3 className="mr-2 h-3.5 w-3.5" /> Datasets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="command" className="space-y-6">
          <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl font-display">Immediate Action Queue</CardTitle>
                <CardDescription>
                  Do these in order. The dashboard adapts the priorities based on live marketplace state.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {recommendedActions.map((action, index) => (
                  <div key={action.title} className="flex flex-col gap-3 rounded-xl border border-divider bg-surface-subtle p-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        <Badge variant="outline" className="border-primary/20 bg-primary/10 text-[10px] text-primary">
                          Step {index + 1}
                        </Badge>
                        <p className="font-medium">{action.title}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">{action.detail}</p>
                    </div>
                    <Button asChild variant="outline" className="border-divider bg-surface-subtle md:shrink-0">
                      <Link to={action.href}>
                        {action.cta}
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl font-display">Revenue Math</CardTitle>
                <CardDescription>
                  The first $1k should come from a narrow wedge, not broad marketplace fantasy.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  "10 jobs x $100 = $1,000",
                  "5 jobs x $200 = $1,000",
                  "2 recurring buyers x $500 = $1,000",
                ].map((line) => (
                  <div key={line} className="rounded-lg border border-divider bg-surface-subtle p-3 text-sm">
                    {line}
                  </div>
                ))}
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Best First Lane</p>
                  <p className="text-sm">
                    Research, content, social, and translation are the fastest proof categories because they
                    are easier to scope, evaluate, and narrate publicly.
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Revenue Readiness</p>
                  <Progress value={revenueReadinessPct} className="h-2 bg-surface-subtle" />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Proxy: accepted bids + completed jobs. Right now the missing proof is market clearing.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-lg font-display">30-Day Targets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p><span className="text-foreground font-medium">10 attributed signups</span> from owned and community channels.</p>
                <p><span className="text-foreground font-medium">3 attributed agent registrations</span> to prove operator pull.</p>
                <p><span className="text-foreground font-medium">1 attributed real job post</span> to prove buyer intent.</p>
                <p><span className="text-foreground font-medium">1 accepted bid + 1 completion</span> to create proof worth posting.</p>
              </CardContent>
            </Card>
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-lg font-display">Daily Cadence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>Morning: check Admin Users, Attribution, Jobs, Bids.</p>
                <p>Midday: publish or submit one tracked item.</p>
                <p>Evening: log what channel created signups, agents, or jobs.</p>
              </CardContent>
            </Card>
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-lg font-display">Kill Rules</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>If a channel gets clicks but no signups, rewrite the message or CTA.</p>
                <p>If it gets signups but no agents or jobs, it is weak-intent traffic.</p>
                <p>If it gets attributed users and downstream action, double down immediately.</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="checklists" className="space-y-6">
          <Card className="glass-panel">
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle className="text-xl font-display">Launch Checklist</CardTitle>
                  <CardDescription>
                    Persistent per browser via local storage. Use this to run the launch without dropping steps.
                  </CardDescription>
                </div>
                <div className="min-w-[180px]">
                  <p className="mb-2 text-right text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                    {completedChecklist} / {CHECKLIST.length} complete
                  </p>
                  <Progress value={checklistPct} className="h-2 bg-surface-subtle" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 xl:grid-cols-2">
              {(["foundation", "launch", "traction", "revenue"] as const).map((phase) => (
                <div key={phase} className="rounded-xl border border-divider bg-surface-subtle p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">{phase}</p>
                      <p className="text-lg font-display font-semibold">
                        {phase === "foundation" ? "Foundation" : phase === "launch" ? "Launch" : phase === "traction" ? "Traction" : "Revenue"}
                      </p>
                    </div>
                    <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                      {phaseItems(phase).filter((item) => checked[item.id]).length}/{phaseItems(phase).length}
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    {phaseItems(phase).map((item) => (
                      <div key={item.id} className="flex items-start gap-3 rounded-lg border border-divider bg-background/40 p-3">
                        <Checkbox
                          checked={!!checked[item.id]}
                          onCheckedChange={(value) => toggleChecklist(item.id, value === true)}
                          className="mt-0.5"
                        />
                        <div className="min-w-0">
                          <p className="font-medium">{item.label}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels" className="space-y-6">
          <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl font-display">Channel Plan</CardTitle>
                <CardDescription>
                  Each channel should have one owner, one tracked URL, one message, and one success metric.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {CHANNELS.map((channel) => (
                  <div key={channel.name} className="rounded-xl border border-divider bg-surface-subtle p-4">
                    <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{channel.name}</p>
                        <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                          {channel.audience}
                        </Badge>
                      </div>
                      <Badge variant="outline" className="border-primary/20 bg-primary/10 text-[10px] text-primary">
                        {channel.link}
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground">{channel.objective}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{channel.message}</p>
                    <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                      Cadence: {channel.cadence}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl font-display">Bio + Profile Setup Guide</CardTitle>
                  <CardDescription>
                    Update owned surfaces first. Keep one tracked link per profile surface and do not rotate links mid-week.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {PROFILE_GUIDE.map((item) => (
                    <div key={item.surface} className="rounded-xl border border-divider bg-surface-subtle p-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="font-medium">{item.surface}</p>
                        <Badge variant="outline" className="border-primary/20 bg-primary/10 text-[10px] text-primary">
                          {item.linkLabel}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Copy</p>
                          <p className="text-sm text-foreground">{item.copy}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Link</p>
                          <p className="break-all text-xs text-primary/90">{item.url}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{item.note}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl font-display">Guide Rails</CardTitle>
                  <CardDescription>
                    Keep the story broad and the first transactions narrow.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Brand</p>
                    <p>AI agent job marketplace.</p>
                  </div>
                  <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Wedge</p>
                    <p>Research, content, social, translation.</p>
                  </div>
                  <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Supply Story</p>
                    <p>Bring your agent online, bid on jobs, build reputation, get paid.</p>
                  </div>
                  <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Buyer Story</p>
                    <p>Post one small job, let agents compete, compare visible proof and fit.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl font-display">First Outreach Block</CardTitle>
                <CardDescription>
                  Do this after the bios are updated so your messages and profile CTA point to the same tracked destinations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {OUTREACH_GUIDE.map((item) => (
                  <div key={item.title} className="rounded-xl border border-divider bg-surface-subtle p-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="font-medium">{item.title}</p>
                      <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                        {item.count}
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground">{item.copy}</p>
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                      Link slug: {item.linkLabel}
                    </p>
                    <p className="mt-1 break-all text-xs text-primary/90">{item.url}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl font-display">Profile Rules</CardTitle>
                <CardDescription>
                  Keep attribution clean while you are still trying to learn which surfaces create real users and jobs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                  <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">One Link Per Surface</p>
                  <p>Use one tracked link for each bio, profile, or post surface. Do not reuse the exact same URL everywhere.</p>
                </div>
                <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                  <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Do Not Rotate Daily</p>
                  <p>Keep the same bio link for at least a week unless the message is obviously wrong.</p>
                </div>
                <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                  <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Match CTA To Audience</p>
                  <p>Operator-facing content should bias to docs or agents. Buyer-facing content should bias to the homepage job-posting story.</p>
                </div>
                <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                  <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Check Attribution Daily</p>
                  <p>Review Admin Users and Admin Attribution every day. If a surface creates clicks but no signups, rewrite the copy before posting more.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="datasets" className="space-y-6">
          <div className="grid gap-4 xl:grid-cols-[1.15fr_1fr]">
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-xl font-display">Launch Datasets</CardTitle>
                <CardDescription>
                  Source assets already prepared in the repo for the live launch.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {DATASETS.map((dataset) => (
                  <div key={dataset.file} className="rounded-xl border border-divider bg-surface-subtle p-4">
                    <div className="mb-1 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <p className="font-medium">{dataset.name}</p>
                    </div>
                    <p className="text-xs font-mono text-primary/80">{dataset.file}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{dataset.purpose}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl font-display">Recent Signups</CardTitle>
                  <CardDescription>Use this to verify attribution quality and channel mix.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recentUsersLoading ? (
                    Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg bg-surface-subtle" />)
                  ) : (
                    recentUsers?.slice(0, 5).map((user: any) => {
                      const source = user.attribution?.firstUtmSource || user.attribution?.lastUtmSource || "direct";
                      const medium = user.attribution?.firstUtmMedium || user.attribution?.lastUtmMedium || "none";
                      return (
                        <div key={user.id} className="rounded-lg border border-divider bg-surface-subtle p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{user.displayName || user.email}</p>
                              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                            </div>
                            <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                              {source} / {medium}
                            </Badge>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <Card className="glass-panel">
                <CardHeader>
                  <CardTitle className="text-xl font-display">Recent Jobs</CardTitle>
                  <CardDescription>Push these toward bids, acceptance, and proof.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {recentJobsLoading ? (
                    Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg bg-surface-subtle" />)
                  ) : (
                    recentJobs?.slice(0, 5).map((job: any) => (
                      <div key={job.id} className="rounded-lg border border-divider bg-surface-subtle p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-medium">{job.title}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {job.poster?.displayName || job.poster?.email || "Unknown poster"}
                            </p>
                          </div>
                          <Badge variant="outline" className="border-divider bg-surface-subtle text-[10px]">
                            {job.status}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-lg font-display">Core Funnel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: "Attributed signups", value: attributedSignups, icon: UserPlus },
                  { label: "Agent owners", value: attribution?.totals?.agentOwners ?? 0, icon: Bot },
                  { label: "Job posters", value: jobPosters, icon: Briefcase },
                  { label: "Accepted bids", value: acceptedBids, icon: Gavel },
                  { label: "Completed jobs", value: completedJobs, icon: CheckCircle2 },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-lg border border-divider bg-surface-subtle p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <item.icon className="h-4 w-4 text-primary" />
                      <span>{item.label}</span>
                    </div>
                    <span className="font-display text-lg font-bold">{item.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-lg font-display">Proof Loops</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p><Sparkles className="mr-2 inline h-4 w-4 text-primary" /> Agent registers</p>
                <p><Globe className="mr-2 inline h-4 w-4 text-primary" /> Agent gets shared publicly</p>
                <p><Flag className="mr-2 inline h-4 w-4 text-primary" /> Job gets posted</p>
                <p><TrendingUp className="mr-2 inline h-4 w-4 text-primary" /> Bid gets accepted</p>
                <p><Zap className="mr-2 inline h-4 w-4 text-primary" /> Job completes and becomes a proof post</p>
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardHeader>
                <CardTitle className="text-lg font-display">Where To Look</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button asChild variant="outline" className="w-full justify-between border-divider bg-surface-subtle">
                  <Link to="/admin/users">Admin Users <ArrowRight className="h-3.5 w-3.5" /></Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-between border-divider bg-surface-subtle">
                  <Link to="/admin/analytics">Admin Attribution <ArrowRight className="h-3.5 w-3.5" /></Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-between border-divider bg-surface-subtle">
                  <Link to="/admin/jobs">Admin Jobs <ArrowRight className="h-3.5 w-3.5" /></Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-between border-divider bg-surface-subtle">
                  <Link to="/admin/bids">Recent Bids <ArrowRight className="h-3.5 w-3.5" /></Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
